/**
 * Calculate degrees of separation from a focal person
 * Uses breadth-first search to find shortest path to each member
 *
 * @param {Array} members - Array of member objects with relationship data
 * @param {Array} relationships - Array of relationship objects
 * @param {number} focalPersonId - ID of the focal person
 * @returns {Map<number, {degree: number, via: string}>} Map of memberId to degree info
 */
export function calculateDegrees(members, relationships, focalPersonId) {
  const degrees = new Map();

  if (!focalPersonId || members.length === 0) {
    return degrees;
  }

  // Build adjacency map from relationships
  const adjacency = new Map();
  members.forEach(m => adjacency.set(m.id, []));

  relationships.forEach(rel => {
    const { person1_id, person2_id, relationship_type } = rel;

    if (adjacency.has(person1_id) && adjacency.has(person2_id)) {
      // Priority order: spouse (0) > co-parent (1) > parent/child/sibling (2)
      const priority = relationship_type === 'spouse' ? 0
                     : relationship_type === 'co-parent' ? 1
                     : 2;

      adjacency.get(person1_id).push({ id: person2_id, type: relationship_type, priority });
      adjacency.get(person2_id).push({ id: person1_id, type: relationship_type, priority });
    }
  });

  // BFS from focal person
  const queue = [{ id: focalPersonId, degree: 0, via: 'self' }];
  degrees.set(focalPersonId, { degree: 0, via: 'self' });

  while (queue.length > 0) {
    const { id: currentId, degree: currentDegree } = queue.shift();
    const neighbors = adjacency.get(currentId) || [];

    // Sort neighbors by priority (spouse first, then co-parent, then others)
    neighbors.sort((a, b) => a.priority - b.priority);

    for (const neighbor of neighbors) {
      if (!degrees.has(neighbor.id)) {
        const newDegree = currentDegree + 1;
        degrees.set(neighbor.id, { degree: newDegree, via: neighbor.type });
        queue.push({ id: neighbor.id, degree: newDegree });
      }
    }
  }

  return degrees;
}

/**
 * Filter members by maximum degree of separation
 *
 * @param {Array} members - Array of member objects
 * @param {Map} degreeMap - Map from calculateDegrees
 * @param {number} maxDegree - Maximum degree to include (null for all)
 * @returns {Array} Filtered array of members
 */
export function filterByDegree(members, degreeMap, maxDegree) {
  if (maxDegree === null || maxDegree === undefined) {
    return members;
  }

  return members.filter(m => {
    const info = degreeMap.get(m.id);
    return info && info.degree <= maxDegree;
  });
}

/**
 * Filter relationships to only include those between filtered members
 *
 * @param {Array} relationships - Array of relationship objects
 * @param {Set} memberIds - Set of member IDs to include
 * @returns {Array} Filtered relationships
 */
export function filterRelationships(relationships, memberIds) {
  return relationships.filter(rel =>
    memberIds.has(rel.person1_id) && memberIds.has(rel.person2_id)
  );
}
